'use client';
import { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { useUser } from '@/shared/providers/user-provider/use-user';

export const STORAGE_KEY = 'confirmDataPrompts';
const POPUP_DELAY_MS = 24 * 60 * 60 * 1000;
export const LOCAL_STORAGE_UPDATED_EVENT = 'confirmLocalStorageUpdated';

type PromptState = {
  viewCount: number;
  lastPopupTime: number | null;
  showPopup: boolean;
  showBanner: boolean;
  sessionToken: string;
};

type UserStorageEntry = {
  userId: string | number;
  state: PromptState;
};

export default function ConfirmInitializer() {
  const { user } = useUser();
  const userId = user?.id || '';
  const currentSessionToken = Cookies.get('sessionId') || '';
  const isInitialMount = useRef(true);

  const [state, setState] = useState<PromptState>({
    viewCount: 0,
    lastPopupTime: null,
    showPopup: false,
    showBanner: false,
    sessionToken: '',
  });

  const updateStateFromStorage = () => {
    if (!userId) return;

    const saved = localStorage.getItem(STORAGE_KEY);
    const storageArray: UserStorageEntry[] = saved ? JSON.parse(saved) : [];
    const userEntry = storageArray.find((entry) => entry.userId === userId);

    if (userEntry) {
      setState(userEntry.state);
    }
  };

  useEffect(() => {
    if (!userId) return;

    const now = Date.now();
    const saved = localStorage.getItem(STORAGE_KEY);
    const storageArray: UserStorageEntry[] = saved ? JSON.parse(saved) : [];

    const currentUserIndex = storageArray.findIndex(
      (entry) => entry.userId === userId
    );
    const currentUserEntry =
      currentUserIndex !== -1 ? storageArray[currentUserIndex] : null;

    let updatedState: PromptState;

    if (currentUserEntry) {
      updatedState = { ...currentUserEntry.state };

      if (currentSessionToken !== updatedState.sessionToken) {
        updatedState.sessionToken = currentSessionToken;
      }

      if (isInitialMount.current) {
        updatedState.viewCount += 1;
        isInitialMount.current = false;
      }
    } else {
      updatedState = {
        viewCount: 1,
        lastPopupTime: null,
        showPopup: false,
        showBanner: false,
        sessionToken: currentSessionToken,
      };
      isInitialMount.current = false;
    }

    updatedState = {
      ...updatedState,
      showPopup: updatedState.viewCount === 2,
      lastPopupTime:
        updatedState.viewCount === 2 ? now : updatedState.lastPopupTime,
      showBanner:
        updatedState.viewCount >= 3 &&
        updatedState.lastPopupTime &&
        now - updatedState.lastPopupTime >= POPUP_DELAY_MS,
    };

    const updatedStorage = [...storageArray];

    if (currentUserIndex !== -1) {
      updatedStorage[currentUserIndex] = { userId, state: updatedState };
    } else {
      updatedStorage.push({ userId, state: updatedState });
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedStorage));
    setState(updatedState);

    window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_UPDATED_EVENT));
  }, [userId, currentSessionToken]);

  useEffect(() => {
    if (!userId) return;

    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        updateStateFromStorage();
      }
    };

    const handleCustomEvent = () => {
      updateStateFromStorage();
    };

    window.addEventListener('storage', handleStorageEvent);
    window.addEventListener(LOCAL_STORAGE_UPDATED_EVENT, handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
      window.removeEventListener(
        LOCAL_STORAGE_UPDATED_EVENT,
        handleCustomEvent
      );
    };
  }, [userId]);

  return null;
}
